import { useMemo } from "react";
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  Circle,
  Map as GoogleMap,
  Pin,
} from "@vis.gl/react-google-maps";

import { FitBounds } from "@/components/maps/FitBounds";
import { GoogleMapsGate } from "@/components/maps/GoogleMapsGate";
import type { GeoPosition } from "@/hooks/useGeolocation";
import type { EmployeeSite } from "@/lib/employee-api";
import { GOOGLE_MAPS_MAP_ID, primaryColor } from "@/lib/google-maps";
import { padForRadius, type Point } from "@/lib/map-geometry";

/** The employee's own position, in the app's "you are here" blue. */
const YOU_COLOR = "#0ea5e9";

/**
 * The confirmation's map (FEAT-2026-025): the site's geofence, and where the
 * reading puts the employee with a circle for its accuracy — the web
 * counterpart of the map on the app's check-in screen.
 *
 * Decoration only. The verdict beside it is the server's (BR-153), not the
 * geometry drawn here: a WFH or WFA check-in is allowed outside the circle.
 */
export function PunchMap({ site, position }: { site: EmployeeSite | null; position: GeoPosition }) {
  const color = useMemo(() => primaryColor(), []);
  const { latitude, longitude } = position;
  // A browser that cannot place the user may report an accuracy of whole
  // kilometres; drawing that swallows the map, so it is left out of the fit.
  const accuracy =
    Number.isFinite(position.accuracy) && position.accuracy > 0 ? position.accuracy : null;

  const you = useMemo<Point>(() => ({ lat: latitude, lng: longitude }), [latitude, longitude]);
  const fit = useMemo(() => {
    const out: Point[] = [you];
    if (accuracy != null && accuracy <= 2_000) out.push(...padForRadius(you, accuracy));
    if (site) out.push(...padForRadius({ lat: site.latitude, lng: site.longitude }, site.geofenceRadiusM));
    return out;
  }, [you, accuracy, site]);

  const enforced = site?.geofenceMode === "GPS_AND_GEOFENCE";

  return (
    <GoogleMapsGate className="h-48 w-full">
      <div className="h-48 w-full overflow-hidden rounded-lg border">
        <GoogleMap
          mapId={GOOGLE_MAPS_MAP_ID}
          defaultCenter={you}
          defaultZoom={17}
          gestureHandling="cooperative"
          disableDefaultUI
          zoomControl
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
        >
          <FitBounds points={fit} />

          {site && (
            <>
              {/* As on the admin map: a geofence nobody is refused by draws fainter. */}
              <Circle
                center={{ lat: site.latitude, lng: site.longitude }}
                radius={site.geofenceRadiusM}
                clickable={false}
                strokeColor={color}
                strokeWeight={enforced ? 2 : 1}
                strokeOpacity={enforced ? 0.9 : 0.45}
                fillColor={color}
                fillOpacity={enforced ? 0.12 : 0.05}
              />
              <AdvancedMarker position={{ lat: site.latitude, lng: site.longitude }} title={site.name}>
                <Pin background={color} borderColor="#ffffff" glyphColor="#ffffff" />
              </AdvancedMarker>
            </>
          )}

          {accuracy != null && (
            <Circle
              center={you}
              radius={accuracy}
              clickable={false}
              strokeColor={YOU_COLOR}
              strokeWeight={1}
              strokeOpacity={0.5}
              fillColor={YOU_COLOR}
              fillOpacity={0.12}
            />
          )}
          <AdvancedMarker position={you} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} title="You">
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 9999,
                background: YOU_COLOR,
                border: "3px solid white",
                boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
              }}
            />
          </AdvancedMarker>
        </GoogleMap>
      </div>
    </GoogleMapsGate>
  );
}
